import React from 'react';
import { PatientDetailsPatientProp, PatientDetailsSection } from './internal';
import { intl, intlCountry } from '../../lib';
import { VBox } from '../layout';
import { isEmptyString } from '../../util';
import { infrastructurePortalUtil } from 'generated-ts-common';

/**
 * Creates a readable string from the given patient Address. When the passed address is
 * null or undefined, an empty string will be returned.
 *
 * @param address the address to format
 * @return the full formatted address of the patient. An empty string if the passed address is null, undefined or contains no valid information.
 * @author agd
 */
export const getFullPatientAddress = (
  address:
    | infrastructurePortalUtil.saf.infrastructure.model.patient.Address
    | undefined
    | null
): string => {
  if (!address) {
    return '';
  }

  let addressString = '';
  if (!isEmptyString(address.streetName))
    addressString += address.streetName + ', ';
  if (!isEmptyString(address.dwellingNumber))
    addressString += address.dwellingNumber + ', ';
  if (!isEmptyString(address.postalCode))
    addressString += address.postalCode + ' ';
  if (!isEmptyString(address.city)) addressString += address.city + ', ';
  if (!isEmptyString(address.country))
    addressString += intlCountry(address.country);

  // trim and remove trailing comma
  addressString = addressString.trim();
  if (addressString.charAt(addressString.length - 1) === ',')
    addressString = addressString.substring(0, addressString.length - 1);

  return addressString;
};

/**
 * Props for a {@link PatientDetailsAddress} component.
 * @author agd
 */
export type PatientDetailsAddressProps = PatientDetailsPatientProp;

/**
 * Component for displaying the address details of a patient.
 *
 * @param patient the patient whose data is being displayed
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function PatientDetailsAddress({
  patient
}: PatientDetailsAddressProps): JSX.Element {
  // get the legal address, since that is the only one we care about
  const legalAddress = patient.addresses?.find(
    addr => addr.addressType === 'L'
  );

  // Hide the entire section if there is no address
  if (legalAddress === undefined) {
    return <></>;
  }

  return (
    <PatientDetailsSection
      className="ehs-patient-details-address"
      title={intl('view.patient.address')}
    >
      <VBox>
        <span className="address-label">
          <b>{intl('view.patient.detail.legalAddress')}</b>
        </span>
        <span className="address-text">
          {getFullPatientAddress(legalAddress)}
        </span>
      </VBox>
    </PatientDetailsSection>
  );
}
